import { useFormikContext } from 'formik';
import { useEffect, useMemo } from 'react';
import { CurrencyIcon } from 'src/modules/currencies/components/CurrencyIcon';
import { useCurrencyList } from 'src/modules/currencies/hooks/useCurrencies';
import { Currency } from 'src/modules/currencies/types';

import { Select, SelectContent, SelectFieldTrigger, SelectItem, SelectValue } from '@/design-system/components/ui';

import PriceReferenceCallout from './PriceReferenceCallout';

interface PriceReferenceSelectProps {
  chainIdentifier: string;
  fieldName: string;
  label?: string;
  onCurrencyChange?: (currency: Currency) => void;
  autoSelectFirst?: boolean;
  showCallout?: boolean;
  calloutTriggerType?: string;
}

export default function PriceReferenceSelect({
  chainIdentifier,
  fieldName,
  label = 'Price reference',
  onCurrencyChange,
  autoSelectFirst = false,
  showCallout = false,
  calloutTriggerType = 'trigger',
}: PriceReferenceSelectProps) {
  const { values, setFieldValue, errors, touched } = useFormikContext<Record<string, unknown>>();

  const { currencies } = useCurrencyList({ chainIdentifier, priceReference: true }, !!chainIdentifier);

  const fieldValue = values[fieldName] as string | undefined;
  const fieldError = (touched as Record<string, boolean>)[fieldName] && !!(errors as Record<string, string>)[fieldName];
  const errorMessage = fieldError ? String((errors as Record<string, string>)[fieldName]) : undefined;

  const selectedCurrency = useMemo(
    () => currencies.find((currency) => currency.address === fieldValue) ?? null,
    [fieldValue, currencies],
  );

  useEffect(() => {
    if (autoSelectFirst && currencies.length > 0 && !fieldValue) {
      setFieldValue(fieldName, currencies[0].address);
    }
  }, [autoSelectFirst, currencies, fieldValue, setFieldValue, fieldName]);

  const handleChange = (value: string) => {
    setFieldValue(fieldName, value);
    if (onCurrencyChange) {
      const currency = currencies.find((c) => c.address === value);
      if (currency) onCurrencyChange(currency);
    }
  };

  return (
    <div className="tw:flex tw:flex-col tw:gap-4">
      <Select value={fieldValue ?? ''} onValueChange={handleChange}>
        <SelectFieldTrigger label={label} error={fieldError} errorMessage={errorMessage}>
          <SelectValue placeholder="Select a token" />
        </SelectFieldTrigger>
        <SelectContent>
          {currencies.map((currency) => (
            <SelectItem key={currency.address} value={currency.address}>
              <span className="tw:flex tw:items-center tw:gap-2">
                <CurrencyIcon logoUri={currency.logoUri} name={currency.name} size={20} />
                <span>
                  {currency.name}{' '}
                  <span className="tw:text-xs tw:text-muted-foreground">{currency.decimals} decimals</span>
                </span>
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {showCallout && (
        <PriceReferenceCallout
          selectedCurrencyName={selectedCurrency?.name}
          selectedCurrencyDecimals={selectedCurrency?.decimals}
          hasSelectedCurrency={!!fieldValue}
          triggerType={calloutTriggerType}
        />
      )}
    </div>
  );
}
